import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import Config from 'config';
import FacilityAPI from 'api/facility';
import CreditAPI from 'api/credit';
import ReservationAPI from 'api/reservation';
import FormatUtils from 'utils/format';
import StripeUtils from 'utils/stripe';
import SentryUtils from '@spothero/utils/sentry';
import {userUpdate} from '../user/user-actions';

export const CHECKOUT_FETCH_RATES = 'CHECKOUT_FETCH_RATES';
export const CHECKOUT_SHOW_CHANGE_DATE_TIME = 'CHECKOUT_SHOW_CHANGE_DATE_TIME';
export const CHECKOUT_HIDE_CHANGE_DATE_TIME = 'CHECKOUT_HIDE_CHANGE_DATE_TIME';
export const CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS =
    'CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS';
export const CHECKOUT_UPDATE_DATA = 'CHECKOUT_UPDATE_DATA';
export const CHECKOUT_USER_CHANGE = 'CHECKOUT_USER_CHANGE';

export const CHECKOUT_SHOW_RATE_DETAILS = 'CHECKOUT_SHOW_RATE_DETAILS';
export const CHECKOUT_HIDE_RATE_DETAILS = 'CHECKOUT_HIDE_RATE_DETAILS';
export const CHECKOUT_TOGGLE_RATE_OVERSIZED_FEE_DETAILS =
    'CHECKOUT_TOGGLE_RATE_OVERSIZED_FEE_DETAILS';
export const CHECKOUT_TOGGLE_RATE_ONLINE_COMMUTER_DETAILS =
    'CHECKOUT_TOGGLE_RATE_ONLINE_COMMUTER_DETAILS';
export const CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS =
    'CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS';
export const CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS =
    'CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS';

export const CHECKOUT_PAYMENT_TOKEN_REQUESTED =
    'CHECKOUT_PAYMENT_TOKEN_REQUESTED';
export const CHECKOUT_PAYMENT_ERROR = 'CHECKOUT_PAYMENT_ERROR';
export const CHECKOUT_ON_PURCHASE = 'CHECKOUT_ON_PURCHASE';
export const CHECKOUT_PURCHASE_ERROR = 'CHECKOUT_PURCHASE_ERROR';
export const CHECKOUT_CLEAR_CUSTOMER = 'CHECKOUT_CLEAR_CUSTOMER';

const purchaseError = (dispatch, errors, extra) => {
    let newState;

    if (errors[0].code === 'previously_purchased_monthly') {
        const {created, parkingPassURL} = JSON.parse(errors[0].messages[0]);

        newState = {
            redundantMonthlyReservationModalDate: created,
            redundantMonthlyReservationModalParkingPassURL: parkingPassURL,
            showRedundantMonthlyReservationModal: true,
        };
    } else {
        const messages = [];

        errors.forEach(error => {
            if (isObject(error.messages[0])) {
                if (!isUndefined(error.messages[0].all_errors)) {
                    messages.push(error.messages[0].all_errors[0]);
                }
            } else {
                messages.push(error.messages[0]);
            }
        });

        if (
            errors[0].code === 'invalid_referral' &&
            extra &&
            !isUndefined(extra.spothero_credit)
        ) {
            dispatch(
                userUpdate({
                    credit: extra.spothero_credit,
                })
            );
        }

        newState = {
            checkoutError: {
                subtitle: `Correct the following mistakes then press Pay & Reserve again. Don't worry, you won't be charged twice.`,
                errors: messages,
            },
        };
    }

    return dispatch({
        type: CHECKOUT_PURCHASE_ERROR,
        payload: newState,
    });
};

export const fetchRates = ({
    spotId,
    starts,
    ends,
    airport,
    eventPackageId,
    includeFacility,
    meta,
}) => {
    return (dispatch, getState) => {
        const {
            searchRequest: {eid: eventId, monthly},
            checkout: {vehicleInfoId, vehicleProfileId},
        } = getState();

        return dispatch({
            type: CHECKOUT_FETCH_RATES,
            payload: FacilityAPI.getRates({
                spotId,
                starts,
                ends,
                eventId,
                monthly,
                airport,
                eventPackageId,
                includeFacility,
                vehicleInfoId,
                vehicleProfileId,
            }),
            meta: {
                ...meta,
                spotId,
                starts,
                ends,
                eventId,
                monthly,
                airport,
                timezone: getState().city.data.timezone,
            },
        });
    };
};

export const showChangeDateTime = ({
    title = 'Edit Time',
    message = 'Just FYI, the price or availability might change if you choose different options here.',
    buttonText = null,
    isStaleStart = false,
} = {}) => {
    return {
        type: CHECKOUT_SHOW_CHANGE_DATE_TIME,
        payload: {
            title,
            message,
            buttonText,
            isStaleStart,
        },
    };
};

export const showChangeDateTimeForScan2payLanding = ({
    title = 'How long do you need to park?',
    message = '',
    buttonText = 'Continue to Checkout',
    isStaleStart = false,
} = {}) => {
    return {
        type: CHECKOUT_SHOW_CHANGE_DATE_TIME,
        payload: {
            title,
            message,
            buttonText,
            isStaleStart,
        },
    };
};

export const hideChangeDateTime = (showRecommendedSpots = false) => {
    return {
        type: CHECKOUT_HIDE_CHANGE_DATE_TIME,
        payload: {
            showRecommendedSpots,
        },
    };
};

export const hideRecommendedSpots = (showChangeDateTimeModal = false) => {
    return {
        type: CHECKOUT_HIDE_RECOMMENDED_SPOTS_DETAILS,
        payload: {
            showChangeDateTimeModal,
        },
    };
};

export const updateData = (data, meta) => {
    return {
        type: CHECKOUT_UPDATE_DATA,
        payload: data,
        meta,
    };
};

export const userChange = ({
    user,
    userNormalized,
    isMonthly,
    extraReservationItems,
}) => {
    return {
        type: CHECKOUT_USER_CHANGE,
        payload: {
            user,
            userNormalized,
            isMonthly,
            extraReservationItems,
        },
    };
};

export const showRateDetails = (section = null) => {
    return {
        type: CHECKOUT_SHOW_RATE_DETAILS,
        payload: {
            section: isString(section) ? section : null,
        },
    };
};

export const hideRateDetails = () => {
    return {
        type: CHECKOUT_HIDE_RATE_DETAILS,
    };
};

export const toggleOversizedFeeDetails = () => {
    return {
        type: CHECKOUT_TOGGLE_RATE_OVERSIZED_FEE_DETAILS,
    };
};

export const toggleOnlineCommuterDetails = () => {
    return {
        type: CHECKOUT_TOGGLE_RATE_ONLINE_COMMUTER_DETAILS,
    };
};

export const toggleTimeExtendedDetails = () => {
    return {
        type: CHECKOUT_TOGGLE_TIME_EXTENDED_DETAILS,
    };
};

export const hideRedundantMonthlyDetails = () => {
    return {
        type: CHECKOUT_HIDE_REDUNDANT_MONTHLY_DETAILS,
    };
};

export const submitPromo = ({
    email,
    price,
    promoCode,
    ruleGroupId,
    ruleType,
    spotId,
    currencyType: facilityCurrencyType,
}) => {
    return (dispatch, getState) => {
        const {
            checkout: {purchaseOnBehalfOfCustomer},
            searchRequest: {starts, ends},
        } = getState();
        const meta = {
            email,
            price,
            promoCode,
            ruleGroupId,
            ruleType,
            spotId,
            currencyType: facilityCurrencyType,
            starts,
            ends,
        };

        const data = CreditAPI.checkPromoCode(meta)
            .then(
                ({
                    currencyType: promoCurrencyType,
                    codeType,
                    discountAmount,
                    percentageOff,
                    percentageOffDiscountMax,
                    ...response
                }) => {
                    const currencySymbol = FormatUtils.currencySymbol(
                        facilityCurrencyType
                    );
                    const discountCoversCharge = price - discountAmount <= 0;
                    const discountDisplayAmount = `${currencySymbol}${discountAmount}`;

                    return {
                        paymentRequired: !(
                            discountCoversCharge && purchaseOnBehalfOfCustomer
                        ),
                        paymentRequiredNoCharge:
                            discountCoversCharge && !purchaseOnBehalfOfCustomer,
                        promoCode: {
                            ...response,
                            codeType,
                            discountAmount,
                            percentageOff,
                            code: promoCode.toUpperCase(),
                            discountDisplayAmount,
                            promoCodeDetails: percentageOff
                                ? `${percentageOff}% off${
                                      percentageOffDiscountMax
                                          ? ` up to ${currencySymbol}${percentageOffDiscountMax}`
                                          : ''
                                  }`
                                : discountDisplayAmount,
                        },
                        incomingPromoCode: null,
                    };
                }
            )
            .catch(err => {
                try {
                    const message = err.data.data.errors.shift().messages[0];

                    return Promise.reject(new Error(message));
                } catch (error) {
                    SentryUtils.sendMessage({
                        message: error.message,
                        level: 'error',
                    });

                    return Promise.reject(
                        new Error('Sorry, this promo code is not enabled.')
                    );
                }
            });

        return dispatch(updateData(data, meta));
    };
};

export const purchase = ({reservationData, isAdmin, onSuccess}) => {
    return (dispatch, getState) => {
        const {
            selectedCreditCard,
            newCardInfo,
            purchaseForCustomerDescription,
            initialReservationId,
        } = reservationData;
        const {
            user: {data: userData},
            searchRequest: {
                monthly: isMonthly,
                rebook_reservation_id: rebookReservationId,
                starts: monthlyStarts,
                eid: eventId,
            },
            spot: {
                selected: {facility, selectedRate},
            },
            checkout,
        } = getState();
        const checkoutData = {
            user: userData,
            isMonthly,
            monthlyStarts,
            eventId,
            purchaseForCustomerDescription,
            initialReservationId,
            ...checkout,
            ...reservationData,
        };

        if (checkout.isRebookingReservation && rebookReservationId) {
            return dispatch({
                type: CHECKOUT_ON_PURCHASE,
                payload: ReservationAPI.rebook({
                    checkoutData,
                    selectedRate,
                    facility,
                    rebookReservationId,
                    onError: (errors, extra) => {
                        purchaseError(dispatch, errors, extra);
                    },
                }),
            });
        } else if (selectedCreditCard === 'new') {
            dispatch({
                type: CHECKOUT_PAYMENT_TOKEN_REQUESTED,
            });

            StripeUtils.createToken({
                isAdmin,
                onSuccess(tokenId) {
                    return dispatch({
                        type: CHECKOUT_ON_PURCHASE,
                        payload: ReservationAPI.add({
                            checkoutData,
                            selectedRate,
                            monthlyStarts,
                            facility,
                            checkoutStartTime: checkout.checkoutStartTime,
                            stripeToken: tokenId,
                            source: Config.isMobile ? 'mobile-web' : 'web',
                            setNewCardAsDefault: newCardInfo.isDefault,
                            onSuccess,
                            onError: (errors, extra) => {
                                purchaseError(dispatch, errors, extra);
                            },
                        }),
                    });
                },
                onError(errorMessage) {
                    return dispatch({
                        type: CHECKOUT_PAYMENT_ERROR,
                        payload: {
                            errors: [errorMessage],
                        },
                    });
                },
            });
        } else {
            const isPurchasingWithPaymentRequest =
                checkoutData.stripePaymentType &&
                checkoutData.stripePaymentType !== 'cc';

            return dispatch({
                type: CHECKOUT_ON_PURCHASE,
                payload: ReservationAPI.add({
                    checkoutData,
                    selectedRate,
                    monthlyStarts,
                    facility,
                    checkoutStartTime: checkout.checkoutStartTime,
                    source: Config.isMobile ? 'mobile-web' : 'web',
                    ...(isPurchasingWithPaymentRequest && {
                        stripePaymentType: checkoutData.stripePaymentType,
                    }),
                    onSuccess,
                    onError: (errors, extra) => {
                        purchaseError(dispatch, errors, extra);
                    },
                }),
                meta: {
                    isPurchasingWithPaymentRequest,
                },
            });
        }
    };
};

export const clearCustomer = () => {
    return (dispatch, getState) => {
        const {
            checkout: {adminUser},
            user: {data: user},
        } = getState();

        dispatch({
            type: CHECKOUT_CLEAR_CUSTOMER,
            payload: !isEmpty(adminUser) ? adminUser : user,
        });
    };
};
